import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Plus, Trash2, Edit2, X, Check } from "lucide-react";
import { toast } from "sonner";

const CATS = [
  { key: "chicken", label: "Chicken" },
  { key: "mutton", label: "Mutton" },
  { key: "offals", label: "Offals" },
];

export default function Products() {
  const [products, setProducts] = useState([]);
  const [name, setName] = useState("");
  const [category, setCategory] = useState("chicken");
  const [unit, setUnit] = useState("kg");
  const [editing, setEditing] = useState(null);
  const [editName, setEditName] = useState("");
  const [editCategory, setEditCategory] = useState("chicken");
  const [editUnit, setEditUnit] = useState("kg");

  const load = async () => {
    const res = await api.get("/products");
    setProducts(res.data);
  };

  useEffect(() => {
    load();
  }, []);

  const add = async () => {
    if (!name.trim()) return;
    await api.post("/products", { name, category, unit });
    toast.success("Product added");
    setName("");
    setUnit("kg");
    load();
  };

  const startEdit = (p) => {
    setEditing(p.id);
    setEditName(p.name);
    setEditCategory(p.category);
    setEditUnit(p.unit || "kg");
  };

  const saveEdit = async () => {
    await api.put(`/products/${editing}`, {
      name: editName,
      category: editCategory,
      unit: editUnit,
      active: true,
    });
    setEditing(null);
    load();
  };

  const del = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/products/${id}`);
    load();
  };

  const toggleActive = async (p) => {
    await api.put(`/products/${p.id}`, {
      name: p.name,
      category: p.category,
      unit: p.unit || "kg",
      active: !p.active,
    });
    load();
  };

  const grouped = products.reduce((acc, p) => {
    if (!acc[p.category]) acc[p.category] = [];
    acc[p.category].push(p);
    return acc;
  }, {});

  return (
    <div className="p-4 md:p-8 max-w-5xl mx-auto page-enter" data-testid="products-page">
      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Catalog</div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tighter mt-1">
          Products
        </h1>
      </div>

      <div className="border border-zinc-200 bg-white p-5 mb-6">
        <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-3">Add New</div>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
          <input
            placeholder="Product name (e.g., Chicken Wings)"
            value={name}
            onChange={(e) => setName(e.target.value)}
            data-testid="product-name-input"
            className="md:col-span-6 border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-600"
          />
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            data-testid="product-category-select"
            className="md:col-span-4 border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-600"
          >
            {CATS.map((c) => (
              <option key={c.key} value={c.key}>
                {c.label}
              </option>
            ))}
          </select>
          <button
            onClick={add}
            data-testid="product-add-btn"
            className="md:col-span-2 bg-sky-600 text-white px-4 py-2 text-sm uppercase tracking-wider hover:bg-sky-700 flex items-center justify-center gap-1"
          >
            <Plus className="h-4 w-4" />
            Add
          </button>
        </div>
      </div>

      {CATS.map((c) => (
        <div key={c.key} className="mb-6">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">
            {c.label}
          </div>
          <div className="border border-zinc-200 bg-white">
            {(grouped[c.key] || []).length === 0 ? (
              <div className="px-5 py-4 text-sm text-zinc-500">No products</div>
            ) : (
              (grouped[c.key] || []).map((p, i, arr) => (
                <div
                  key={p.id}
                  className={`flex items-center justify-between px-5 py-3 ${
                    i < arr.length - 1 ? "border-b border-zinc-200" : ""
                  }`}
                  data-testid={`product-row-${p.id}`}
                >
                  {editing === p.id ? (
                    <>
                      <div className="flex-1 flex gap-2">
                        <input
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          className="flex-1 border border-zinc-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-sky-600"
                        />
                        <select
                          value={editCategory}
                          onChange={(e) => setEditCategory(e.target.value)}
                          className="border border-zinc-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-sky-600"
                        >
                          {CATS.map((c) => (
                            <option key={c.key} value={c.key}>{c.label}</option>
                          ))}
                        </select>
                        <select
                          value={editUnit}
                          onChange={(e) => setEditUnit(e.target.value)}
                          className="border border-zinc-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-sky-600"
                        >
                          <option value="kg">Kg</option>
                          <option value="piece">Piece</option>
                        </select>
                      </div>
                      <div className="flex gap-1 ml-2">
                        <button onClick={saveEdit} className="text-emerald-600 hover:text-emerald-700 p-1">
                          <Check className="h-4 w-4" />
                        </button>
                        <button onClick={() => setEditing(null)} className="text-zinc-400 hover:text-zinc-700 p-1">
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className={`text-sm font-medium ${!p.active ? "line-through text-zinc-400" : ""}`}>
                        {p.name}
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => toggleActive(p)}
                          className={`text-[10px] uppercase tracking-wider px-2 py-1 ${
                            p.active
                              ? "bg-emerald-50 text-emerald-700"
                              : "bg-zinc-100 text-zinc-500"
                          }`}
                        >
                          {p.active ? "Active" : "Inactive"}
                        </button>
                        <button
                          onClick={() => startEdit(p)}
                          className="text-zinc-400 hover:text-zinc-900 p-1"
                          data-testid={`product-edit-${p.id}`}
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => del(p.id)}
                          className="text-zinc-400 hover:text-sky-600 p-1"
                          data-testid={`product-delete-${p.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
